import { Badge, Box, Flex, HStack, Text } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { DeleteIcon } from '@resistapp/client/components/icons/delete-icon';
import { oldTheme } from '@resistapp/client/components/shared/old-styles';
import { Loading } from '@resistapp/client/components/states/loading';
import { useAdminUserDirectory, useDeleteUser, useOrganisations, useProjects } from '@resistapp/client/hooks/api';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import type { AdminUserDirectoryUser, EmailDeliveryStatus, EmailDeliverySummary } from '@resistapp/common/api-types';
import { Feature } from '@resistapp/common/features';
import { buildOrganisationInheritanceSnapshotById } from '@resistapp/common/organisation-inheritance';
import { Project } from '@resistapp/common/types';
import { Dictionary, keyBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

export function AllUsersSection() {
  const { data: projects } = useProjects();
  const { data: organisations } = useOrganisations();
  const [projectsById, setProjectsById] = useState<Dictionary<Project>>({});
  const { data: directory, loading, error, send: load } = useAdminUserDirectory();

  const organisationInheritanceById = useMemo(
    () => buildOrganisationInheritanceSnapshotById(organisations ?? []),
    [organisations],
  );

  const adminUserIds = useMemo(() => {
    return new Set<number>(
      (directory?.organisationUsers ?? [])
        .filter(membership =>
          organisationInheritanceById.get(membership.organisationId)?.features.includes(Feature.ADMIN),
        )
        .map(membership => membership.userId),
    );
  }, [directory, organisationInheritanceById]);

  const users = useMemo(() => {
    return directory?.users.filter(user => !adminUserIds.has(user.id));
  }, [adminUserIds, directory]);

  const projectIdsByUserId = useMemo(() => {
    if (!directory) {
      return new Map<number, number[]>();
    }

    const projectIdSetsByUserId = new Map<number, Set<number>>();
    directory.organisationUsers.forEach(membership => {
      if (adminUserIds.has(membership.userId)) {
        return;
      }
      const organisationSnapshot = organisationInheritanceById.get(membership.organisationId);
      if (!organisationSnapshot) {
        return;
      }
      const existing = projectIdSetsByUserId.get(membership.userId) ?? new Set<number>();
      organisationSnapshot.projectAccesses.forEach(projectId => {
        existing.add(projectId);
      });
      projectIdSetsByUserId.set(membership.userId, existing);
    });

    return new Map(
      Array.from(projectIdSetsByUserId.entries(), ([userId, projectIds]) => [
        userId,
        Array.from(projectIds)
          .filter(id => Number.isFinite(id))
          .sort((a, b) => b - a),
      ]),
    );
  }, [adminUserIds, directory, organisationInheritanceById]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line exhaustive-deps-with-refs/exhaustive-deps
  }, []);

  useEffect(() => {
    if (projects) {
      setProjectsById(keyBy(projects, project => project.id));
    }
  }, [projects]);

  if (error) {
    throw error;
  }

  return (
    <Flex direction="column">
      <p style={{ paddingLeft: oldTheme.spacing.l, paddingRight: oldTheme.spacing.l, paddingTop: oldTheme.spacing.l }}>
        This section lists all non-admin users, together with their project accesses inherited via organisations.
      </p>
      <ListView>
        {loading || !users ? (
          <Box padding={2}>
            <Loading />
          </Box>
        ) : (
          users.map((user, key) => (
            <UserListItem
              key={`u-${key}`}
              user={user}
              projectsById={projectsById}
              projectIds={projectIdsByUserId.get(user.id) ?? []}
            />
          ))
        )}
      </ListView>
    </Flex>
  );
}

interface UserListItemProps {
  user: AdminUserDirectoryUser;
  projectsById?: Partial<Dictionary<Project>>;
  projectIds: number[];
}

function UserListItem(props: UserListItemProps) {
  const { user, projectsById, projectIds } = props;
  const navigate = useNavigateWithQuery();
  const { send } = useDeleteUser(user.id);
  return (
    <li>
      <span>
        {user.firstName} {user.lastName}, {user.email}
        <DeleteIcon triggerDelete={async () => await send()} name={user.email} />
      </span>
      <EmailDeliverySummaryRow emailDelivery={user.latestVerificationEmail} label="Verification" />
      <EmailDeliverySummaryRow emailDelivery={user.latestPasswordResetEmail} label="Password reset" />
      <ul>
        {projectsById
          ? projectIds.map((projectId, usKey) => {
              return (
                <li
                  key={`us-${usKey}`}
                  style={{ marginLeft: oldTheme.spacing.m, cursor: 'pointer', color: 'gray' }}
                  onClick={() => {
                    navigate(`/research/${projectId}`, false);
                  }}
                >
                  {projectsById[projectId]?.name || projectId}
                </li>
              );
            })
          : null}
      </ul>
    </li>
  );
}

const emailDeliveryStatusMetaByStatus: Record<EmailDeliveryStatus, { label: string; colorScheme: string }> = {
  NOT_SENT: { label: 'Not sent', colorScheme: 'orange' },
  SEND_FAILED: { label: 'Send failed', colorScheme: 'red' },
  SENT: { label: 'Accepted by SendGrid', colorScheme: 'blue' },
  PROCESSED: { label: 'Processed', colorScheme: 'cyan' },
  DELIVERED: { label: 'Delivered', colorScheme: 'green' },
  DEFERRED: { label: 'Deferred', colorScheme: 'yellow' },
  BOUNCE: { label: 'Bounced', colorScheme: 'red' },
  DROPPED: { label: 'Dropped', colorScheme: 'red' },
};

function formatEmailDeliveryDateTime(isoDate: string): string {
  const parsed = new Date(isoDate);
  return Number.isNaN(parsed.getTime()) ? isoDate : parsed.toLocaleString();
}

function EmailDeliverySummaryRow(props: { emailDelivery: EmailDeliverySummary | null; label: string }) {
  const { emailDelivery, label } = props;
  if (!emailDelivery) {
    return null;
  }
  const statusMeta = emailDeliveryStatusMetaByStatus[emailDelivery.deliveryStatus];
  const statusAt = emailDelivery.deliveryStatusAt || emailDelivery.sentAt || emailDelivery.createdAt;
  return (
    <HStack spacing={2} marginTop={1} marginLeft={oldTheme.spacing.m}>
      <Text as="span" fontSize="sm" color="gray.600">
        {label}
      </Text>
      <Badge colorScheme={statusMeta.colorScheme}>{statusMeta.label}</Badge>
      <Text as="span" fontSize="sm" color="gray.600">
        {formatEmailDeliveryDateTime(statusAt)}
      </Text>
      {emailDelivery.deliveryStatusReason ? (
        <Text as="span" fontSize="sm" color="gray.600">
          {emailDelivery.deliveryStatusReason}
        </Text>
      ) : null}
    </HStack>
  );
}

const ListView = styled.ul`
  padding-top: ${oldTheme.spacing.m};
  padding-left: ${oldTheme.spacing.xl};
`;
